import React, { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { showToast } from "../constants";

const PRODUCTION_PUBLIC_BASE_URL = "https://admin.4homeskw.net";

const getPublicBaseUrl = () => {
  if (process.env.REACT_APP_PUBLIC_BASE_URL) {
    return process.env.REACT_APP_PUBLIC_BASE_URL.replace(/\/$/, "");
  }

  const { hostname, origin } = window.location;
  if (hostname === "localhost" || hostname.includes("dev.")) {
    return origin;
  }

  return PRODUCTION_PUBLIC_BASE_URL;
};

type PublicLinkRow = {
  key: string;
  label: string;
  path: string;
};

type PublicLinksCardProps = {
  title: string;
  description: string;
  pageLabel: string;
  urlLabel: string;
  copyLabel: string;
  openLabel: string;
  copiedMessage: string;
  links: PublicLinkRow[];
};

const PublicLinksCard: React.FC<PublicLinksCardProps> = ({
  title,
  description,
  pageLabel,
  urlLabel,
  copyLabel,
  openLabel,
  copiedMessage,
  links,
}) => {
  const baseUrl = useMemo(() => getPublicBaseUrl(), []);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = async (key: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      showToast(copiedMessage);
      window.setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      showToast("Unable to copy URL");
    }
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: 2.5,
        border: "1px solid #e8eaf0",
        borderRadius: 2,
        backgroundColor: "#fafbfc",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        {description}
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{pageLabel}</TableCell>
              <TableCell>{urlLabel}</TableCell>
              <TableCell align="center" width={120}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map((link) => {
              const url = `${baseUrl}${link.path}`;
              return (
                <TableRow key={link.key}>
                  <TableCell>{link.label}</TableCell>
                  <TableCell>
                    <Typography
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "primary.main",
                        textDecoration: "none",
                        wordBreak: "break-all",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {url}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={copyLabel}>
                      <IconButton
                        size="small"
                        aria-label={copyLabel}
                        onClick={() => handleCopy(link.key, url)}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={openLabel}>
                      <IconButton
                        size="small"
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={openLabel}
                      >
                        <OpenInNewRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {copiedKey === link.key ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 0.5, color: "success.main" }}
                      >
                        {copiedMessage}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PublicLinksCard;
